import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import HomeHeaderBanner from "../../features/home/components/HomeHeaderBanner";
import HomeActionCarousel from "../../features/home/components/HomeActionCarousel";
import HomeServicesGrid from "../../features/home/components/HomeServicesGrid";
import HomeCareCard from "../../features/home/components/HomeCareCard";
import HomeQuickMariaChatModal from "../../features/home/components/HomeQuickMariaChatModal";

import TodayCareSummaryDrawer from "../../features/home/components/TodayCareSummaryDrawer";
import NotificationsDrawer from "../../features/notifications/NotificationsDrawer";
import MeasuresModal from "../../features/readings/components/MeasuresModal";

import { useHomeSummary } from "../../features/home/hooks/useHomeSummary";
import { useHomeMeasures } from "../../features/home/hooks/useHomeMeasures";
import { useHomeData } from "../../features/home/hooks/useHomeData";

export default function AppHome() {
  const navigate = useNavigate();
  const [quickMariaOpen, setQuickMariaOpen] = useState(false);

  const {
    loading: summaryLoading,
    data,
    handleQuickAction,
    summaryDrawerOpen,
    setSummaryDrawerOpen,
    notificationsDrawerOpen,
    setNotificationsDrawerOpen,
  } = useHomeSummary();

  const {
    loading: dataLoading,
    userName,
    firstName,
    profile,
    sex,
    doseSummary,
    todayDoseTotal,
    todayDoseDone,
    waterCups,
    carePercent,
    handleAddWater,
  } = useHomeData({ navigate });

  const {
    measuresModalOpen,
    measuresSaving,
    measuresError,
    measuresSuccess,
    entryDate,
    setEntryDate,
    weightKg,
    weightNotes,
    setWeightKg,
    setWeightNotes,
    measuresForm,
    setField,
    openMeasuresModal,
    closeMeasuresModal,
    handleSaveMeasures,
  } = useHomeMeasures({ sex });

  const handleCardQuickAction = (quickAction, card) => {
    if (quickAction?.type === "navigate" || card?.route) {
      if (card?.route) {
        navigate(card.route);
      } else {
        handleQuickAction(quickAction, card);
      }
    } else if (quickAction?.type === "openModal" || quickAction?.action === "openModal") {
      openMeasuresModal();
    } else {
      handleQuickAction(quickAction, card);
    }
  };

  const loading = summaryLoading && dataLoading;

  if (loading) {
    return (
      <div className="space-y-4 py-2 animate-pulse">
        <div className="h-10 w-40 rounded-xl bg-purple-100" />
        <div className="h-24 rounded-3xl bg-purple-200 w-full" />
        <div className="h-20 rounded-3xl bg-purple-100 w-full" />
        <div className="grid grid-cols-3 gap-2">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="h-24 rounded-2xl bg-purple-100" />
          ))}
        </div>
      </div>
    );
  }

  const displayName = firstName || userName || "Claudia";
  const displayDoneDoses = todayDoseTotal > 0 ? todayDoseDone : 3;
  const displayTotalDoses = todayDoseTotal > 0 ? todayDoseTotal : 4;
  const displayPercent = Math.round(
    carePercent > 0 ? carePercent * 100 : (data.progress?.percent || 75)
  );

  const pendingDose = data.todayItems?.find((it) => !it.completed) || {
    name: "Tirzepatida (Dose Semanal)",
    dosage: "5.0 mg",
    time: "14:00",
  };

  return (
    <div className="space-y-4 pb-2">
      {/* 1. BANNER HERO EM 4 PARTES: [Foto MarIA 💬] [Olá, Clau 👋] [🩺 120/80 / 💊 3/4 ok] [75% HOJE] */}
      <HomeHeaderBanner
        userName={displayName}
        pressure="120/80"
        dosesDone={displayDoneDoses}
        dosesTotal={displayTotalDoses}
        percent={displayPercent}
        onOpenMariaChat={() => setQuickMariaOpen(true)}
        onOpenNotifications={() => setNotificationsDrawerOpen(true)}
        onOpenSummary={() => setSummaryDrawerOpen(true)}
        onNavigateRoutine={() => navigate("/app/routine")}
        onNavigateEvolution={() => navigate("/app/evolution")}
        hasUnreadNotifications={Boolean(data.notifications?.unreadCount)}
      />

      {/* 2. CARROSSEL DE AÇÕES RÁPIDAS (DOSES COM CONFETE 🎉, ÁGUA +250ML E PESAGEM) */}
      <HomeActionCarousel
        pendingDose={pendingDose}
        onApplyDose={(d) => handleCardQuickAction({ action: "apply_dose" }, d)}
        waterCups={waterCups}
        waterGoal={8}
        onAddWater={handleAddWater}
        onOpenMeasures={openMeasuresModal}
        onNavigateRoutine={() => navigate("/app/routine")}
      />

      {/* 3. GRID 3x3 DE SERVIÇOS & MONITORAMENTO (9 ÁREAS) */}
      <HomeServicesGrid
        onOpenMariaChat={() => setQuickMariaOpen(true)}
        onOpenMeasuresModal={openMeasuresModal}
        pendingDoseCount={doseSummary?.pending || (displayTotalDoses - displayDoneDoses)}
        lowStockCount={1}
        currentWeight={profile?.currentWeight ? `${profile.currentWeight}kg` : "64.5kg"}
        bloodPressure="120/80"
        waterAmount={`${(waterCups || 6) * 250}ml`}
      />

      {/* 4. CARDS DE CUIDADOS SIMPLIFICADOS (STATUS FLUSH-RIGHT E NÚMEROS EM NEGRITO) */}
      {data?.cards && data.cards.length > 0 && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between px-1 mb-1">
            <h2 className="text-xs font-black uppercase tracking-wider text-[#6D6680]">
              Cuidados & Medições
            </h2>
            <span className="text-[11px] font-bold text-[#6C54D8]">
              {data.cards.length} monitoramentos
            </span>
          </div>

          {data.cards.map((card) => (
            <HomeCareCard
              key={card.id || card.type}
              card={card}
              onQuickAction={(type) => handleCardQuickAction({ action: type }, card)}
            />
          ))}
        </div>
      )}

      {/* 4. MODAIS E DRAWERS DE APOIO */}
      <HomeQuickMariaChatModal
        open={quickMariaOpen}
        onClose={() => setQuickMariaOpen(false)}
        userName={displayName}
        onAddWaterQuick={handleAddWater}
      />

      <TodayCareSummaryDrawer
        open={summaryDrawerOpen}
        onClose={() => setSummaryDrawerOpen(false)}
        todayItems={data.todayItems}
        cards={data.cards}
      />

      <NotificationsDrawer
        open={notificationsDrawerOpen}
        onClose={() => setNotificationsDrawerOpen(false)}
      />

      <MeasuresModal
        open={measuresModalOpen}
        onClose={closeMeasuresModal}
        onSave={handleSaveMeasures}
        saving={measuresSaving}
        error={measuresError}
        success={measuresSuccess}
        sex={sex}
        entryDate={entryDate}
        setEntryDate={setEntryDate}
        weightKg={weightKg}
        weightNotes={weightNotes}
        setWeightKg={setWeightKg}
        setWeightNotes={setWeightNotes}
        form={measuresForm}
        setField={setField}
      />
    </div>
  );
}